import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref, get, update, push, remove, set, onValue } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import { MessageCircle, ArrowLeft, Star, ShieldCheck, Share2, Clock, X, Heart, Flag, MapPin, Tag, Eye, Lock, Bell, BellOff, QrCode } from "lucide-react";
import { canSeePhoto } from "../utils/photoPrivacy";
import { QRCodeSVG } from "qrcode.react";

const SAFE_ZONES = [
  "📚 College Library Entrance",
  "🍽️ Main Canteen",
  "🏛️ Admin Block Reception",
  "🚪 College Main Gate",
  "☕ Coffee Shop",
  "🏋️ Gym / Sports Complex",
];

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile, isAdmin } = useAuth();
  const [listing, setListing] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);

  // Rating state
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingHover, setRatingHover] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [ratingSubmitted, setRatingSubmitted] = useState(false);

  // Saved/Wishlist
  const [saved, setSaved] = useState(false);

  // Report
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDone, setReportDone] = useState(false);

  // Price offer
  const [showOffer, setShowOffer] = useState(false);
  const [offerPrice, setOfferPrice] = useState("");
  const [offerSent, setOfferSent] = useState(false);
  const [offerLoading, setOfferLoading] = useState(false);
  const [offerError, setOfferError] = useState("");

  // Image gallery
  const [activeImgIdx, setActiveImgIdx] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const galleryTouchX = useRef(null);
  const lightboxTouchX = useRef(null);

  // Safe zones
  const [showZones, setShowZones] = useState(true);

  // Buy Now countdown (seconds remaining)
  const [countdown, setCountdown] = useState(null);

  // Cross-college buy requests
  const [myRequest, setMyRequest] = useState(null); // current user's request
  const [pendingRequests, setPendingRequests] = useState([]); // for seller
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");
  const [requestLoading, setRequestLoading] = useState(false);

  // Offers
  const [pendingOffers, setPendingOffers] = useState([]);
  const [myOffer, setMyOffer] = useState(null);
  const [counterOfferMap, setCounterOfferMap] = useState({});
  const [counterPriceMap, setCounterPriceMap] = useState({});

  // Copy link feedback
  const [linkCopied, setLinkCopied] = useState(false);

  // Price Drop Alert
  const [priceAlert, setPriceAlert] = useState(false);
  const [alertSaving, setAlertSaving] = useState(false);

  // Seller reviews
  const [sellerReviews, setSellerReviews] = useState([]);

  // Similar listings
  const [similarListings, setSimilarListings] = useState([]);

  // Chat loading
  const [chatLoading, setChatLoading] = useState(false);

  // Buyer receipt confirmation
  const [confirmingReceipt, setConfirmingReceipt] = useState(false);

  // Seller block
  const [sellerBlocked, setSellerBlocked] = useState(false);
  const [canSeeSellerPhoto, setCanSeeSellerPhoto] = useState(true);

  // Rental booking
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookStartDate, setBookStartDate] = useState("");
  const [bookEndDate, setBookEndDate] = useState("");
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [bookingDone, setBookingDone] = useState(false);
  const [myBooking, setMyBooking] = useState(null); // existing booking by currentUser

  // WhatsApp request
  const [myWARequest, setMyWARequest] = useState(null);       // buyer's own request
  const [pendingWARequests, setPendingWARequests] = useState([]); // for seller view
  const [waRequestLoading, setWARequestLoading] = useState(false);

  // QR Code
  const [showQR, setShowQR] = useState(false);

  // People watching (saves count)
  const [savesCount, setSavesCount] = useState(0);

  // Real-time listener for WA requests so buyer sees approval instantly
  useEffect(() => {
    if (!currentUser || !id) return;
    const waRef = ref(db, `waRequests/${id}`);
    const unsub = onValue(waRef, (snap) => {
      if (snap.exists()) {
        const allWA = Object.entries(snap.val()).map(([wid, w]) => ({ wid, ...w }));
        setMyWARequest(allWA.find(w => w.buyerId === currentUser.uid) || null);
        setPendingWARequests(allWA.filter(w => w.status === "pending"));
      } else {
        setMyWARequest(null);
        setPendingWARequests([]);
      }
    });
    return () => unsub();
  }, [id, currentUser]);

  useEffect(() => {
    // Real-time listener for the listing itself — price/status/title updates show instantly
    const unsubListing = onValue(ref(db, `listings/${id}`), (snap) => {
      if (snap.exists()) {
        const data = { id, ...snap.val() };
        setListing(data);
        // Save to recently viewed in localStorage
        try {
          const stored = JSON.parse(localStorage.getItem("recentlyViewed") || "[]");
          const filtered = stored.filter(item => item.id !== id);
          const entry = { id, title: data.title, imageUrl: data.imageUrl, price: data.price, college: data.college };
          const updated = [entry, ...filtered].slice(0, 10);
          localStorage.setItem("recentlyViewed", JSON.stringify(updated));
        } catch (e) {}
      }
    });
    return () => unsubListing();
  }, [id]);

  useEffect(() => {
    async function fetchData() {
      try {
        const snap = await get(ref(db, `listings/${id}`));
        if (snap.exists()) {
          let data = { id, ...snap.val() };
          if (data.status === "reserved" && data.reservedUntil && data.reservedUntil < Date.now()) {
            await update(ref(db, `listings/${id}`), { status: "active", reservedBy: null, reservedUntil: null });
          }
          if (data.status === "pending_purchase" && data.buyingUntil && data.buyingUntil < Date.now()) {
            await update(ref(db, `listings/${id}`), { status: "active", buyingBy: null, buyingUntil: null });
            if (data.sellerId) {
              await push(ref(db, `notifications/${data.sellerId}`), {
                type: "buy_expired",
                message: `Purchase window expired for "${data.title}" — listing is active again.`,
                listingId: id, createdAt: Date.now(), read: false,
              });
            }
          }
          // Track unique views per user (24h dedup using localStorage)
          const viewKey = `cs_view_${id}_${currentUser?.uid || "guest"}`;
          const lastViewed = localStorage.getItem(viewKey);
          if (!lastViewed || Date.now() - Number(lastViewed) > 24 * 60 * 60 * 1000) {
            await update(ref(db, `listings/${id}`), { views: (data.views || 0) + 1 });
            localStorage.setItem(viewKey, String(Date.now()));
          }
          if (data.sellerId) {
            const sellerSnap = await get(ref(db, `users/${data.sellerId}`));
            if (sellerSnap.exists()) {
              const sellerData = sellerSnap.val();
              setSeller(sellerData);
              // Check photo privacy
              const visible = await canSeePhoto(data.sellerId, sellerData, currentUser?.uid || null);
              setCanSeeSellerPhoto(visible);
            }
          }
          // Check if saved
          if (currentUser) {
            const savedSnap = await get(ref(db, `saved/${currentUser.uid}/${id}`));
            setSaved(savedSnap.exists());
            // Check if seller is blocked
            if (data.sellerId) {
              const blockSnap = await get(ref(db, `blocked/${currentUser.uid}/${data.sellerId}`));
              setSellerBlocked(blockSnap.exists());
            }
            // Load cross-college requests
            const reqSnap = await get(ref(db, `crossCollegeRequests/${id}`));
            if (reqSnap.exists()) {
              const all = Object.entries(reqSnap.val()).map(([rid, r]) => ({ rid, ...r }));
              // My own request
              const mine = all.find(r => r.buyerId === currentUser.uid);
              if (mine) setMyRequest(mine);
              // Pending requests for seller
              const pending = all.filter(r => r.status === "pending");
              setPendingRequests(pending);
            }
            // Check price alert
            const alertSnap = await get(ref(db, `priceAlerts/${id}/${currentUser.uid}`));
            setPriceAlert(alertSnap.exists() && alertSnap.val() === true);

            // Load offers
            const offersSnap = await get(ref(db, `offers/${id}`));
            if (offersSnap.exists()) {
              const allOffers = Object.entries(offersSnap.val()).map(([oid, o]) => ({ oid, ...o }));
              setMyOffer(allOffers.find(o => o.buyerId === currentUser.uid) || null);
              setPendingOffers(allOffers.filter(o => o.status === "pending"));
            }
            // WA requests handled by real-time listener above
          }
          // Load seller reviews
          if (data.sellerId) {
            const revSnap = await get(ref(db, `reviews/${data.sellerId}`));
            if (revSnap.exists()) {
              const revs = Object.entries(revSnap.val()).map(([rid, r]) => ({ rid, ...r }));
              setSellerReviews(revs.sort((a, b) => b.createdAt - a.createdAt).slice(0, 3));
            }
          }
          // Count how many users saved this listing (people watching)
          try {
            const savedSnap2 = await get(ref(db, "saved"));
            if (savedSnap2.exists()) {
              let count = 0;
              Object.values(savedSnap2.val()).forEach(userSaved => {
                if (userSaved && userSaved[id]) count++;
              });
              setSavesCount(count);
            }
          } catch (e) {}

          // Load similar listings (same category)
          if (data.category) {
            const allSnap = await get(ref(db, "listings"));
            if (allSnap.exists()) {
              const similar = Object.entries(allSnap.val())
                .map(([sid, s]) => ({ id: sid, ...s }))
                .filter(s => s.id !== id && s.status === "active" && s.category === data.category)
                .slice(0, 4);
              setSimilarListings(similar);
            }
          }
        }
      } catch (e) {  }
      setLoading(false);
      // Track recently viewed
      if (currentUser) {
        try {
          const key = `cs_recently_viewed_${currentUser.uid}`;
          const existing = JSON.parse(localStorage.getItem(key) || "[]");
          const updated = [id, ...existing.filter(x => x !== id)].slice(0, 10);
          localStorage.setItem(key, JSON.stringify(updated));
        } catch (e) {}
      }
    }
    fetchData();
  }, [id, currentUser]);

  async function toggleSave() {
    if (!currentUser) return;
    if (saved) {
      await update(ref(db, `saved/${currentUser.uid}`), { [id]: null });
      setSaved(false);
    } else {
      await update(ref(db, `saved/${currentUser.uid}`), { [id]: true });
      setSaved(true);
    }
  }

  async function togglePriceAlert() {
    if (!currentUser) return;
    setAlertSaving(true);
    try {
      if (priceAlert) {
        await set(ref(db, `priceAlerts/${id}/${currentUser.uid}`), null);
        setPriceAlert(false);
      } else {
        await set(ref(db, `priceAlerts/${id}/${currentUser.uid}`), true);
        setPriceAlert(true);
      }
    } catch (e) {  }
    setAlertSaving(false);
  }


  // Opens WhatsApp directly — only called after seller approves
  async function openWhatsApp() {
    let waNumber = listing.whatsapp;
    try {
      if (listing.sellerId) {
        const sellerSnap = await get(ref(db, `users/${listing.sellerId}`));
        if (sellerSnap.exists() && sellerSnap.val().phone) waNumber = sellerSnap.val().phone;
      }
    } catch (e) {}
    const number = waNumber?.replace(/\D/g, "");
    if (!number || number.length < 10) {
      alert("Seller's WhatsApp number is not available. Please use in-app chat.");
      return;
    }
    const message = encodeURIComponent(`Hi! I saw your listing "${listing.title}" for ₹${listing.price} on CampusSwap. Is it still available?`);
    window.open(`https://wa.me/91${number}?text=${message}`, "_blank");
  }

  // Buyer requests WhatsApp access — seller must approve first
  async function requestWhatsApp() {
    if (!currentUser || !listing) return;
    if (myWARequest) return; // already requested
    setWARequestLoading(true);
    try {
      const reqData = {
        buyerId: currentUser.uid,
        buyerName: userProfile?.name || "A buyer",
        buyerCollege: userProfile?.college || "",
        status: "pending",
        createdAt: Date.now(),
        listingId: id,
        listingTitle: listing.title,
      };
      const waRef = await push(ref(db, `waRequests/${id}`), reqData);
      setMyWARequest({ wid: waRef.key, ...reqData });
      // Notify seller
      if (listing.sellerId) {
        await push(ref(db, `notifications/${listing.sellerId}`), {
          type: "whatsapp_request",
          message: `📱 ${userProfile?.name || "A buyer"} wants your WhatsApp for "${listing.title}" — approve to share your number`,
          listingId: id,
          createdAt: Date.now(),
          read: false,
        });
      }
    } catch (e) {  }
    setWARequestLoading(false);
  }

  // Seller approves or declines a WhatsApp request
  async function handleWARequestAction(req, action) {
    await update(ref(db, `waRequests/${id}/${req.wid}`), { status: action });
    setPendingWARequests(prev => prev.filter(r => r.wid !== req.wid));
    const buyerMsg = action === "approved"
      ? `✅ Seller approved your WhatsApp request for "${listing.title}"! You can now chat on WhatsApp.`
      : `Your WhatsApp request for "${listing.title}" was declined. Use in-app chat instead.`;
    await push(ref(db, `notifications/${req.buyerId}`), {
      type: action === "approved" ? "whatsapp_approved" : "whatsapp_declined",
      message: buyerMsg,
      listingId: id,
      createdAt: Date.now(),
      read: false,
    });
  }

  async function createChatWithIndex(buyerId, buyerName, sellerId, sellerName, extraFields = {}) {
    const newChat = await push(ref(db, "chats"), {
      listingId: id,
      listingTitle: listing.title,
      listingImage: listing.imageUrl || null,
      buyerId, buyerName, sellerId, sellerName,
      lastMessage: "",
      lastAt: Date.now(),
      ...extraFields,
    });
    const chatId = newChat.key;
    // Write buyer's own index + listing lookup + seller notification index
    try {
      await update(ref(db), {
        [`userChats/${buyerId}/${chatId}`]: true,
        [`listingChats/${id}/${buyerId}`]: chatId,
        [`sellerChats/${sellerId}/${chatId}`]: true,
      });
    } catch (e) {}
    // Also try seller's userChats (may succeed if rules allow)
    try {
      await update(ref(db), { [`userChats/${sellerId}/${chatId}`]: true });
    } catch (e) {}
    // Mark both parties as contacts (for photo privacy "contacts" mode)
    try {
      await update(ref(db), {
        [`contacts/${buyerId}/${sellerId}`]: true,
        [`contacts/${sellerId}/${buyerId}`]: true,
      });
    } catch (e) {}
    return chatId;
  }

  async function startChat() {
    if (!currentUser) { navigate("/login"); return; }
    if (!listing) return;
    setChatLoading(true);
    try {
      // Check listingChats index for existing chat
      let existingChatId = null;
      try {
        const existingSnap = await get(ref(db, `listingChats/${id}/${currentUser.uid}`));
        if (existingSnap.exists()) {
          const val = existingSnap.val();
          // val must be a string chat ID, not a boolean
          if (typeof val === "string") existingChatId = val;
        }
      } catch (e) {}
      if (existingChatId) {
        navigate(`/chat/${existingChatId}`);
        return;
      }
      // Create new chat
      const chatId = await createChatWithIndex(
        currentUser.uid, userProfile?.name || "Buyer",
        listing.sellerId || "", seller?.name || "Seller"
      );
      navigate(`/chat/${chatId}`);
    } catch (e) {
      alert("Could not start chat: " + (e?.message || "Please try again."));
    } finally {
      setChatLoading(false);
    }
  }

  async function handleInterest() {
    const reservedUntil = Date.now() + 12 * 60 * 60 * 1000;
    await update(ref(db, `listings/${id}`), { status: "reserved", reservedBy: currentUser.uid, reservedUntil });
    setListing(prev => ({ ...prev, status: "reserved", reservedBy: currentUser.uid, reservedUntil }));
    // Notify seller
    if (listing.sellerId) {
      await push(ref(db, `notifications/${listing.sellerId}`), {
        type: "interest",
        message: `Someone is interested in "${listing.title}"!`,
        listingId: id,
        createdAt: Date.now(),
        read: false,
      });
    }
    requestWhatsApp();
  }

  async function cancelReservation() {
    await update(ref(db, `listings/${id}`), { status: "active", reservedBy: null, reservedUntil: null });
    setListing(prev => ({ ...prev, status: "active", reservedBy: null, reservedUntil: null }));
    if (listing.sellerId) {
      await push(ref(db, `notifications/${listing.sellerId}`), {
        type: "not_interested",
        message: `A buyer is no longer interested in "${listing.title}"`,
        listingId: id,
        createdAt: Date.now(),
        read: false,
      });
    }
  }

  async function handleBuyNow() {
    if (!currentUser || !listing) return;
    const buyingUntil = Date.now() + 5 * 60 * 1000;
    await update(ref(db, `listings/${id}`), { status: "pending_purchase", buyingBy: currentUser.uid, buyingUntil });
    setListing(prev => ({ ...prev, status: "pending_purchase", buyingBy: currentUser.uid, buyingUntil }));
    if (listing.sellerId) {
      await push(ref(db, `notifications/${listing.sellerId}`), {
        type: "buy_intent",
        message: `🛍️ ${userProfile?.name || "A buyer"} wants to buy "${listing.title}"! Confirm the sale within 5 minutes.`,
        listingId: id, createdAt: Date.now(), read: false,
      });
    }
    // Auto-open chat so buyer and seller can coordinate payment & meetup
    let chatId = null;
    try {
      const existingSnap = await get(ref(db, `listingChats/${id}/${currentUser.uid}`));
      if (existingSnap.exists()) chatId = existingSnap.val();
    } catch (e) {}
    if (!chatId) {
      chatId = await createChatWithIndex(
        currentUser.uid, userProfile?.name || "Buyer",
        listing.sellerId, seller?.name || "Seller",
        { lastMessage: `🛒 I want to buy "${listing.title}" for ₹${listing.price}. Let's arrange payment and meetup!` }
      );
      await push(ref(db, `chats/${chatId}/messages`), {
        senderId: currentUser.uid,
        text: `🛒 I want to buy "${listing.title}" for ₹${listing.price}. Let's arrange payment and meetup! I suggest we meet at the college campus. You can pay by cash or UPI.`,
        createdAt: Date.now(),
      });
    }
    navigate(`/chat/${chatId}`);
  }

  async function cancelBuyNow() {
    await update(ref(db, `listings/${id}`), { status: "active", buyingBy: null, buyingUntil: null });
    setListing(prev => ({ ...prev, status: "active", buyingBy: null, buyingUntil: null }));
    if (listing.sellerId) {
      await push(ref(db, `notifications/${listing.sellerId}`), {
        type: "buy_cancelled",
        message: `A buyer cancelled their purchase of "${listing.title}" — listing is active again.`,
        listingId: id, createdAt: Date.now(), read: false,
      });
    }
  }

  async function confirmSale() {
    const buyerId = listing.buyingBy;
    await update(ref(db, `listings/${id}`), { status: "sold", buyingBy: null, buyingUntil: null, soldTo: buyerId });
    setListing(prev => ({ ...prev, status: "sold", buyingBy: null, buyingUntil: null, soldTo: buyerId }));
    if (buyerId) {
      await push(ref(db, `notifications/${buyerId}`), {
        type: "sale_confirmed",
        message: `✅ Seller confirmed your purchase of "${listing.title}"! Arrange meet-up details.`,
        listingId: id, createdAt: Date.now(), read: false,
      });
    }
  }

  async function confirmReceipt() {
    setConfirmingReceipt(true);
    try {
      await update(ref(db, `listings/${id}`), { buyerConfirmed: true });
      setListing(prev => ({ ...prev, buyerConfirmed: true }));
      if (listing.sellerId) {
        await push(ref(db, `notifications/${listing.sellerId}`), {
          type: "sale_confirmed",
          message: `✅ Buyer confirmed receipt of "${listing.title}". Transaction complete!`,
          listingId: id,
          createdAt: Date.now(),
          read: false,
        });
      }
    } catch (e) {  }
    setConfirmingReceipt(false);
  }

  async function handleOfferAction(offer, action, counterPrice) {
    if (action === "countered") {
      const cp = Number(counterPrice);
      if (!cp || cp <= 0) return;
      await update(ref(db, `offers/${id}/${offer.oid}`), { status: "countered", counterPrice: cp });
      setPendingOffers(prev => prev.filter(o => o.oid !== offer.oid));
      await push(ref(db, `notifications/${offer.buyerId}`), {
        type: "offer_countered",
        message: `↩️ Seller countered your ₹${offer.price} offer with ₹${cp} for "${listing.title}" — accept or decline?`,
        listingId: id, counterPrice: cp, createdAt: Date.now(), read: false,
      });
      setCounterOfferMap(prev => { const n = {...prev}; delete n[offer.oid]; return n; });
      setCounterPriceMap(prev => { const n = {...prev}; delete n[offer.oid]; return n; });
      return;
    }
    await update(ref(db, `offers/${id}/${offer.oid}`), { status: action });
    setPendingOffers(prev => prev.filter(o => o.oid !== offer.oid));
    if (action === "accepted") {
      await push(ref(db, `notifications/${offer.buyerId}`), {
        type: "offer_accepted",
        message: `✅ Your offer of ₹${offer.price} for "${listing.title}" was accepted! Chat with the seller.`,
        listingId: id, createdAt: Date.now(), read: false,
      });
      const existingSnap = await get(ref(db, `listingChats/${id}/${offer.buyerId}`));
      if (!existingSnap.exists()) {
        await createChatWithIndex(
          offer.buyerId, offer.buyerName,
          listing.sellerId, seller?.name || "Seller",
          { lastMessage: `Offer of ₹${offer.price} accepted!` }
        );
      }
    } else {
      await push(ref(db, `notifications/${offer.buyerId}`), {
        type: "offer_declined",
        message: `Your offer of ₹${offer.price} for "${listing.title}" was declined.`,
        listingId: id, createdAt: Date.now(), read: false,
      });
    }
  }

  async function handleCounterResponse(action) {
    if (!myOffer) return;
    const cp = myOffer.counterPrice;
    if (action === "accepted") {
      await update(ref(db, `offers/${id}/${currentUser.uid}`), { status: "accepted", price: cp });
      setMyOffer(prev => ({ ...prev, status: "accepted", price: cp }));
      await push(ref(db, `notifications/${listing.sellerId}`), {
        type: "offer_accepted",
        message: `✅ Buyer accepted your counter-offer of ₹${cp} for "${listing.title}"!`,
        listingId: id, createdAt: Date.now(), read: false,
      });
      const existingSnap = await get(ref(db, `listingChats/${id}/${currentUser.uid}`));
      if (!existingSnap.exists()) {
        await createChatWithIndex(
          currentUser.uid, userProfile?.name || "Buyer",
          listing.sellerId, seller?.name || "Seller",
          { lastMessage: `Counter-offer of ₹${cp} accepted!` }
        );
      }
    } else {
      await update(ref(db, `offers/${id}/${currentUser.uid}`), { status: "declined" });
      setMyOffer(prev => ({ ...prev, status: "declined" }));
      await push(ref(db, `notifications/${listing.sellerId}`), {
        type: "offer_declined",
        message: `Buyer declined your counter-offer of ₹${cp} for "${listing.title}".`,
        listingId: id, createdAt: Date.now(), read: false,
      });
    }
  }

  // Countdown timer for pending_purchase
  useEffect(() => {
    if (listing?.status !== "pending_purchase" || !listing.buyingUntil) {
      setCountdown(null);
      return;
    }
    const tick = () => {
      const secs = Math.max(0, Math.floor((listing.buyingUntil - Date.now()) / 1000));
      setCountdown(secs);
      if (secs === 0) {
        update(ref(db, `listings/${id}`), { status: "active", buyingBy: null, buyingUntil: null }).then(() => {
          setListing(prev => prev ? { ...prev, status: "active", buyingBy: null, buyingUntil: null } : prev);
        });
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [listing?.status, listing?.buyingUntil]); // eslint-disable-line

  async function submitRating() {
    if (!ratingValue || !listing.sellerId) return;
    // Write review first
    await push(ref(db, `reviews/${listing.sellerId}`), {
      rating: ratingValue,
      comment: ratingComment.trim(),
      buyerId: currentUser.uid,
      listingId: id,
      listingTitle: listing.title,
      createdAt: Date.now(),
    });
    // Recompute seller's average rating from all reviews
    const allRevsSnap = await get(ref(db, `reviews/${listing.sellerId}`));
    if (allRevsSnap.exists()) {
      const revs = Object.values(allRevsSnap.val());
      const avg = revs.reduce((sum, r) => sum + (r.rating || 0), 0) / revs.length;
      await update(ref(db, `users/${listing.sellerId}`), { rating: avg, totalRatings: revs.length });
    }
    await update(ref(db, `listings/${id}`), { buyerRated: true });
    setListing(prev => ({ ...prev, buyerRated: true }));
    setRatingSubmitted(true);
  }

  async function submitReport() {
    if (!reportReason.trim()) return;

    // Prevent duplicate reports from same user
    const existingSnap = await get(ref(db, `reports/${id}`));
    if (existingSnap.exists()) {
      const existing = Object.values(existingSnap.val());
      if (existing.some(r => r.reportedBy === currentUser.uid)) {
        setShowReport(false);
        setReportDone(true);
        return;
      }
    }

    await push(ref(db, `reports/${id}`), {
      reportedBy: currentUser.uid,
      reason: reportReason.trim(),
      listingTitle: listing.title,
      sellerId: listing.sellerId,
      createdAt: Date.now(),
    });

    // Count reports on this listing → auto-hide at 3
    const listingReportsSnap = await get(ref(db, `reports/${id}`));
    const listingReportCount = listingReportsSnap.exists() ? Object.keys(listingReportsSnap.val()).length : 1;
    if (listingReportCount >= 3) {
      await update(ref(db, `listings/${id}`), { status: "hidden", autoHidden: true, autoHiddenAt: Date.now() });
      setListing(prev => ({ ...prev, status: "hidden" }));
    }

    // Count total reports across all seller's listings → auto-suspend at 5
    const sellerId = listing.sellerId;
    try {
      const allListingsSnap = await get(ref(db, "listings"));
      let sellerTotalReports = listingReportCount;
      if (allListingsSnap.exists()) {
        const entries = Object.entries(allListingsSnap.val());
        for (const [lid, l] of entries) {
          if (l.sellerId === sellerId && lid !== id) {
            const rSnap = await get(ref(db, `reports/${lid}`));
            if (rSnap.exists()) sellerTotalReports += Object.keys(rSnap.val()).length;
          }
        }
      }
      if (sellerTotalReports >= 5) {
        await update(ref(db, `users/${sellerId}`), { suspended: true, suspendedAt: Date.now() });
      }
    } catch (e) { /* non-critical */ }

    setShowReport(false);
    setReportDone(true);
  }

  async function sendOffer() {
    const price = Number(offerPrice);
    if (!price || price <= 0) return;
    setOfferLoading(true);
    setOfferError("");
    try {
      const offerData = {
        buyerId: currentUser.uid,
        buyerName: userProfile?.name || "Buyer",
        price,
        status: "pending",
        listingId: id,
        listingTitle: listing.title,
        sellerId: listing.sellerId,
        createdAt: Date.now(),
      };
      // Use UID as key so each buyer can have exactly one offer per listing
      await set(ref(db, `offers/${id}/${currentUser.uid}`), offerData);
      setMyOffer({ oid: currentUser.uid, ...offerData });
      if (listing.sellerId) {
        await push(ref(db, `notifications/${listing.sellerId}`), {
          type: "offer",
          message: `💰 New offer of ₹${price} for "${listing.title}" — tap to accept or decline`,
          listingId: id,
          offerId: currentUser.uid,
          offerPrice: price,
          buyerId: currentUser.uid,
          createdAt: Date.now(),
          read: false,
        });
      }
      setShowOffer(false);
      setOfferSent(true);
      setTimeout(() => setOfferSent(false), 4000);
    } catch (e) {
      console.error("sendOffer failed:", e);
      setOfferError("Failed to send offer. Please try again.");
    } finally {
      setOfferLoading(false);
    }
  }

  // ── Rental price calculation ──────────────────────────────────────────────
  function calcRentalPrice(rental, startDateStr, endDateStr) {
    if (!rental || !startDateStr || !endDateStr) return null;
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    const days = Math.round((end - start) / (1000 * 60 * 60 * 24));
    if (days <= 0) return null;
    let best = null;
    function tryOption(total, desc) {
      if (total <= 0) return;
      if (best === null || total < best.total) best = { total, desc, days };
    }
    if (rental.pricePerDay) tryOption(days * rental.pricePerDay, `${days} day${days > 1 ? "s" : ""} × ₹${rental.pricePerDay}/day = ₹${days * rental.pricePerDay}`);
    if (rental.pricePerWeek && days >= 7) {
      const w = Math.floor(days / 7), rem = days % 7;
      let tot = w * rental.pricePerWeek;
      let desc = `${w} week${w > 1 ? "s" : ""} × ₹${rental.pricePerWeek}/wk`;
      if (rem > 0) { if (rental.pricePerDay) { tot += rem * rental.pricePerDay; desc += ` + ${rem}d × ₹${rental.pricePerDay}/day`; } else { tot = (w + 1) * rental.pricePerWeek; desc = `${w + 1} weeks × ₹${rental.pricePerWeek}/wk (rounded up)`; } }
      tryOption(tot, `${desc} = ₹${tot}`);
    }
    if (rental.pricePerMonth && days >= 30) {
      const m = Math.floor(days / 30), rem = days % 30;
      let tot = m * rental.pricePerMonth;
      let desc = `${m} month${m > 1 ? "s" : ""} × ₹${rental.pricePerMonth}/mo`;
      if (rem > 0) { if (rental.pricePerDay) { tot += rem * rental.pricePerDay; desc += ` + ${rem}d × ₹${rental.pricePerDay}/day`; } else if (rental.pricePerWeek) { const ew = Math.ceil(rem / 7); tot += ew * rental.pricePerWeek; desc += ` + ${ew}wk × ₹${rental.pricePerWeek}/wk`; } else { tot = (m + 1) * rental.pricePerMonth; desc = `${m + 1} months × ₹${rental.pricePerMonth}/mo (rounded up)`; } }
      tryOption(tot, `${desc} = ₹${tot}`);
    }
    return best;
  }

  async function submitRentalBooking() {
    if (!currentUser || !listing || !listing.rental) return;
    const rental = listing.rental;
    if (!bookStartDate || !bookEndDate) { setBookingError("Please select start and end dates."); return; }
    const start = new Date(bookStartDate), end = new Date(bookEndDate);
    const days = Math.round((end - start) / (1000 * 60 * 60 * 24));
    if (days <= 0) { setBookingError("End date must be after start date."); return; }
    if (rental.minDuration && days < rental.minDuration) { setBookingError(`Minimum rental is ${rental.minDuration} day${rental.minDuration > 1 ? "s" : ""}.`); return; }
    if (rental.maxDuration && days > rental.maxDuration) { setBookingError(`Maximum rental is ${rental.maxDuration} days.`); return; }
    if (rental.availableFrom && bookStartDate < rental.availableFrom) { setBookingError(`Item is available from ${new Date(rental.availableFrom).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}.`); return; }
    if (rental.availableTo && bookEndDate > rental.availableTo) { setBookingError(`Item is available until ${new Date(rental.availableTo).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}.`); return; }
    // Check blocked dates
    if (rental.blockedDates) {
      const cur = new Date(bookStartDate);
      while (cur < end) {
        const ds = cur.toISOString().split("T")[0];
        if (rental.blockedDates[ds]) { setBookingError(`Date ${new Date(ds).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} is already booked. Please choose different dates.`); return; }
        cur.setDate(cur.getDate() + 1);
      }
    }
    const priceResult = calcRentalPrice(rental, bookStartDate, bookEndDate);
    if (!priceResult) { setBookingError("Could not calculate price. Check rental pricing is set."); return; }
    setBookingLoading(true); setBookingError("");
    try {
      const bookingRef = await push(ref(db, `rentalBookings/${id}`), {
        renterId: currentUser.uid,
        renterName: userProfile?.name || "Unknown",
        renterPhone: userProfile?.phone || "",
        startDate: bookStartDate,
        endDate: bookEndDate,
        days,
        totalPrice: priceResult.total,
        deposit: rental.deposit || 0,
        deliveryType: rental.deliveryType || "pickup",
        deliveryCharge: rental.deliveryCharge || 0,
        status: "pending",
        createdAt: Date.now(),
        listingTitle: listing.title,
        sellerId: listing.sellerId,
      });
      // Block the dates in listing.rental.blockedDates
      const blockedUpdates = {};
      const cur = new Date(bookStartDate);
      const endD = new Date(bookEndDate);
      while (cur < endD) {
        blockedUpdates[`listings/${id}/rental/blockedDates/${cur.toISOString().split("T")[0]}`] = true;
        cur.setDate(cur.getDate() + 1);
      }
      await update(ref(db), blockedUpdates);
      // Notify seller
      await push(ref(db, `notifications/${listing.sellerId}`), {
        type: "rental_request",
        message: `📅 ${userProfile?.name || "Someone"} wants to rent "${listing.title}" (${bookStartDate} → ${bookEndDate}, ₹${priceResult.total})`,
        listingId: id, bookingId: bookingRef.key, createdAt: Date.now(), read: false,
      });
      setMyBooking({ id: bookingRef.key, startDate: bookStartDate, endDate: bookEndDate, days, totalPrice: priceResult.total, deposit: rental.deposit || 0, status: "pending" });
      setBookingDone(true);
      setShowBookingModal(false);
    } catch (e) { setBookingError("Failed to submit booking. Please try again."); }
    setBookingLoading(false);
  }

  async function sendBuyRequest() {
    if (!currentUser || !listing) return;
    setRequestLoading(true);
    try {
      const newReq = {
        buyerId: currentUser.uid,
        buyerName: userProfile?.name || "Unknown",
        buyerCollege: userProfile?.college || userProfile?.childSchool || "Unknown",
        message: requestMessage.trim(),
        status: "pending",
        createdAt: Date.now(),
        listingTitle: listing.title,
        sellerId: listing.sellerId,
      };
      const reqRef = await push(ref(db, `crossCollegeRequests/${id}`), newReq);
      setMyRequest({ rid: reqRef.key, ...newReq });
      // Notify seller
      await push(ref(db, `notifications/${listing.sellerId}`), {
        type: "cross_college_request",
        message: `${userProfile?.name || "A student"} from ${newReq.buyerCollege} wants to buy your "${listing.title}"`,
        listingId: id,
        createdAt: Date.now(),
        read: false,
      });
      setShowRequestModal(false);
      setRequestMessage("");
    } catch (e) {  }
    setRequestLoading(false);
  }

  async function handleRequestAction(req, action) {
    // action: "approved" | "rejected"
    await update(ref(db, `crossCollegeRequests/${id}/${req.rid}`), { status: action });
    setPendingRequests(prev => prev.filter(r => r.rid !== req.rid));
    // Notify buyer
    const msg = action === "approved"
      ? `✅ Your request to buy "${listing.title}" was approved! You can now contact the seller.`
      : `❌ Your request to buy "${listing.title}" was not approved by the seller.`;
    await push(ref(db, `notifications/${req.buyerId}`), {
      type: action === "approved" ? "request_approved" : "request_rejected",
      message: msg,
      listingId: id,
      createdAt: Date.now(),
      read: false,
    });
  }

  async function toggleBlockSeller() {
    if (!currentUser || !listing) return;
    if (sellerBlocked) {
      await update(ref(db, `blocked/${currentUser.uid}`), { [listing.sellerId]: null });
      setSellerBlocked(false);
    } else {
      if (!window.confirm(`Block this seller? You won't see their listings.`)) return;
      await update(ref(db, `blocked/${currentUser.uid}`), { [listing.sellerId]: true });
      setSellerBlocked(true);
    }
  }

  function handleCopyLink() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }).catch(() => {});
  }

  function handleWhatsAppShare() {
    const text = encodeURIComponent(`🛍️ Check this out on CampusSwap!\n\n*${listing.title}*\n💰 ₹${listing.price === 0 ? "Negotiable" : listing.price}\n📍 ${listing.college}\n\n${window.location.href}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  }

  function handleShare() {
    if (navigator.share) {
      navigator.share({ title: listing.title, text: `Check this out on CampusSwap: ${listing.title} for ₹${listing.price}`, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied!");
    }
  }

  if (loading) return (
    <div className="animate-pulse">
      <div className="h-72 bg-gray-200" />
      <div className="p-4 space-y-3">
        <div className="h-6 bg-gray-200 rounded w-3/4" />
        <div className="h-4 bg-gray-200 rounded w-1/3" />
        <div className="h-16 bg-gray-200 rounded" />
      </div>
    </div>
  );

  if (!listing) return (
    <div className="p-8 text-center text-gray-400 pt-20">
      <p className="text-5xl mb-3">😕</p>
      <p className="font-medium text-gray-600">Listing not found</p>
      <button onClick={() => navigate(-1)} className="mt-4 bg-blue-600 text-white px-5 py-2 rounded-xl text-sm font-semibold">Go Back</button>
    </div>
  );

  return (
    <div className="pb-44 bg-gray-50 min-h-screen">
      {/* Image Gallery — swipeable + tap-to-fullscreen */}
      {(() => {
        const allImages = listing.images?.length ? listing.images : listing.imageUrl ? [listing.imageUrl] : [];
        const safeIdx = Math.min(activeImgIdx, Math.max(0, allImages.length - 1));
        const goPrev = () => setActiveImgIdx(i => Math.max(0, i - 1));
        const goNext = () => setActiveImgIdx(i => Math.min(allImages.length - 1, i + 1));

        return (
          <>
            <div className="relative bg-gray-100 select-none"
              style={{ height: 288 }}
              onTouchStart={e => { galleryTouchX.current = e.touches[0].clientX; }}
              onTouchEnd={e => {
                if (galleryTouchX.current === null) return;
                const dx = e.changedTouches[0].clientX - galleryTouchX.current;
                galleryTouchX.current = null;
                if (dx < -50) goNext();
                else if (dx > 50) goPrev();
              }}
            >
              {/* Main image — tap opens lightbox */}
              {allImages.length > 0
                ? <img
                    src={allImages[safeIdx]}
                    alt={listing.title}
                    className="w-full h-full object-cover cursor-zoom-in"
                    onClick={() => setShowLightbox(true)}
                    draggable={false}
                  />
                : <div className="w-full h-full flex items-center justify-center text-8xl text-gray-200">📦</div>}

              {/* Dot indicators (when no thumbnails) */}
              {allImages.length > 1 && allImages.length <= 5 && (
                <div className="absolute bottom-12 left-0 right-0 flex justify-center gap-1.5">
                  {allImages.map((_, i) => (
                    <button key={i} onClick={() => setActiveImgIdx(i)}
                      className={`rounded-full transition-all ${i === safeIdx ? "w-5 h-2 bg-white" : "w-2 h-2 bg-white/50"}`} />
                  ))}
                </div>
              )}

              {/* Thumbnail strip */}
              {allImages.length > 1 && (
                <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 px-4">
                  {allImages.map((url, i) => (
                    <button key={i} onClick={() => setActiveImgIdx(i)}
                      className={`w-8 h-8 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-all ${i === safeIdx ? "border-white shadow-lg scale-110" : "border-white/40 opacity-60"}`}>
                      <img src={url} alt="" className="w-full h-full object-cover" draggable={false} />
                    </button>
                  ))}
                </div>
              )}

              {/* Counter badge top-right of image */}
              {allImages.length > 1 && (
                <div className="absolute top-14 right-3 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                  {safeIdx + 1}/{allImages.length}
                </div>
              )}

              <button onClick={() => navigate(-1)} className="absolute top-4 left-4 bg-white rounded-full p-2 shadow-md">
                <ArrowLeft size={20} className="text-gray-700" />
              </button>
              <div className="absolute top-4 right-4 flex gap-2">
                {currentUser && listing.sellerId !== currentUser.uid && (
                  <button onClick={toggleSave} className="bg-white rounded-full p-2 shadow-md">
                    <Heart size={18} className={saved ? "text-red-500 fill-red-500" : "text-gray-400"} />
                  </button>
                )}
                <button onClick={handleWhatsAppShare} className="bg-green-500 rounded-full p-2 shadow-md" title="Share on WhatsApp">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </button>
                <button onClick={handleCopyLink} className={`rounded-full p-2 shadow-md transition ${linkCopied ? "bg-green-500" : "bg-white"}`} title="Copy link">
                  {linkCopied
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>}
                </button>
                <button onClick={handleShare} className="bg-white rounded-full p-2 shadow-md">
                  <Share2 size={18} className="text-gray-700" />
                </button>
                <button onClick={() => setShowQR(true)} className="bg-white rounded-full p-2 shadow-md" title="Show QR Code">
                  <QrCode size={18} className="text-gray-700" />
                </button>
              </div>
              <div className="absolute bottom-14 left-3 flex flex-col gap-1">
                {listing.views > 0 && (
                  <div className="bg-black/40 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                    <Eye size={12} /> {listing.views} views
                  </div>
                )}
                {savesCount > 0 && (
                  <div className="bg-red-500/80 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 font-semibold">
                    🔥 {savesCount} {savesCount === 1 ? "person" : "people"} watching
                  </div>
                )}
              </div>
            </div>

            {/* ── Fullscreen Lightbox ── */}
            {showLightbox && allImages.length > 0 && (
              <div
                style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.96)", zIndex: 99999, display: "flex", flexDirection: "column" }}
                onTouchStart={e => { lightboxTouchX.current = e.touches[0].clientX; }}
                onTouchEnd={e => {
                  if (lightboxTouchX.current === null) return;
                  const dx = e.changedTouches[0].clientX - lightboxTouchX.current;
                  lightboxTouchX.current = null;
                  if (dx < -50) setActiveImgIdx(i => Math.min(allImages.length - 1, i + 1));
                  else if (dx > 50) setActiveImgIdx(i => Math.max(0, i - 1));
                }}
              >
                {/* Top bar */}
                <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
                  <span className="text-white/60 text-sm">{safeIdx + 1} / {allImages.length}</span>
                  <button onClick={() => setShowLightbox(false)}
                    className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
                    <X size={20} className="text-white" />
                  </button>
                </div>

                {/* Image — fills remaining space */}
                <div className="flex-1 flex items-center justify-center px-2 min-h-0">
                  <img
                    src={allImages[safeIdx]}
                    alt={listing.title}
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 8 }}
                    draggable={false}
                  />
                </div>

                {/* Thumbnail strip */}
                {allImages.length > 1 && (
                  <div className="flex justify-center gap-2 px-4 py-3 overflow-x-auto flex-shrink-0">
                    {allImages.map((url, i) => (
                      <button key={i} onClick={() => setActiveImgIdx(i)}
                        className={`flex-shrink-0 rounded-xl overflow-hidden border-2 transition-all ${i === safeIdx ? "border-white w-14 h-14 scale-110" : "border-white/25 w-12 h-12 opacity-50"}`}>
                        <img src={url} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}

                {/* Swipe hint */}
                {allImages.length > 1 && (
                  <p className="text-white/30 text-xs text-center pb-safe pb-4">Swipe left / right to navigate</p>
                )}
              </div>
            )}
          </>
        );
      })()}

      {/* QR Code Modal */}
      {showQR && listing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowQR(false)}>
          <div className="bg-white rounded-3xl p-6 max-w-xs w-full text-center" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">Scan to Open</h3>
              <button onClick={() => setShowQR(false)} className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                <X size={16} className="text-gray-600" />
              </button>
            </div>
            <div className="flex justify-center mb-4">
              <QRCodeSVG
                value={window.location.href}
                size={200}
                level="M"
                includeMargin={true}
                imageSettings={{ src: "/logo192.png", height: 36, width: 36, excavate: true }}
              />
            </div>
            <p className="text-sm font-semibold text-gray-700 line-clamp-2 mb-1">{listing.title}</p>
            <p className="text-xs text-gray-400">Share this QR to let others open this listing</p>
            <button
              onClick={() => { navigator.clipboard.writeText(window.location.href); alert("Link copied!"); }}
              className="mt-3 w-full bg-blue-600 text-white py-2.5 rounded-xl text-sm font-semibold"
            >
              Copy Link
            </button>
          </div>
        </div>
      )}

      {/* Guest banner */}
      {!currentUser && (
        <div className="mx-4 mt-3 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl px-4 py-3 flex items-center justify-between gap-3">
          <div>
            <p className="text-white font-bold text-sm">Want to buy this?</p>
            <p className="text-blue-200 text-xs mt-0.5">Join CampusSwap free — chat, offer, save</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <a href="/login" className="bg-white text-blue-600 font-bold text-xs px-3 py-2 rounded-xl">Login</a>
            <a href="/signup" className="bg-yellow-400 text-blue-900 font-bold text-xs px-3 py-2 rounded-xl">Sign Up</a>
          </div>
        </div>
      )}

      <div className="px-4 pt-4 space-y-4 max-w-lg mx-auto">
        {/* Title & Price */}
        <div>
          <div className="flex items-start justify-between gap-2">
            <h1 className="text-xl font-bold text-gray-800 flex-1">{listing.title}</h1>
            {!listing.isRental && (
              <div className="text-right flex-shrink-0">
                <span className="text-2xl font-bold text-blue-600">
                  {listing.price === 0 ? "Free" : `₹${listing.price}`}
                </span>
              </div>
            )}
            {listing.isRental && listing.rental && (
              <div className="text-right flex-shrink-0 space-y-0.5">
                {listing.rental.pricePerDay && <p className="text-base font-bold text-teal-600">₹{listing.rental.pricePerDay}<span className="text-xs font-normal">/day</span></p>}
                {listing.rental.pricePerWeek && <p className="text-sm font-semibold text-teal-500">₹{listing.rental.pricePerWeek}<span className="text-xs font-normal">/wk</span></p>}
                {listing.rental.pricePerMonth && <p className="text-sm font-semibold text-teal-500">₹{listing.rental.pricePerMonth}<span className="text-xs font-normal">/mo</span></p>}
              </div>
            )}
            {listing.isRental && !listing.rental && listing.rentalPeriod && (
              <div className="text-right flex-shrink-0">
                <span className="text-2xl font-bold text-teal-600">₹{listing.price}</span>
                <p className="text-xs text-teal-600 font-semibold mt-0.5">{{ per_day: "/ day", per_week: "/ week", per_month: "/ month" }[listing.rentalPeriod]}</p>
              </div>
            )}
          </div>
          <div className="flex gap-2 mt-2 flex-wrap">
            <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full">{listing.category}</span>
            <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-1 rounded-full">{listing.condition}</span>
            {listing.isRental && <span className="bg-teal-100 text-teal-700 text-xs font-bold px-2.5 py-1 rounded-full">🏠 RENTAL</span>}
            {listing.status === "sold" && <span className="bg-red-100 text-red-700 text-xs font-bold px-2.5 py-1 rounded-full">SOLD</span>}
          </div>
          {offerSent && (
            <div className="mt-2 bg-green-50 border border-green-200 text-green-700 text-xs px-3 py-2 rounded-xl font-medium">
              ✅ Your offer has been sent! Seller will respond via notification.
            </div>
          )}
          {reportDone && (
            <div className="mt-2 bg-orange-50 border border-orange-200 text-orange-700 text-xs px-3 py-2 rounded-xl font-medium">
              ✅ Report submitted. If 3 users report this listing, it will be hidden automatically.
            </div>
          )}
          <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-800">
            ⚠️ <strong>Safety tip:</strong> Never pay in advance. Meet at a safe public place on campus. CampusSwap never asks for payment outside the platform.
          </div>
        </div>

        {/* Bundle Deal info */}
        {listing.isBundle && (
          <div className="bg-purple-50 border border-purple-100 rounded-xl p-4">
            <p className="text-xs font-bold text-purple-700 uppercase tracking-wide mb-2">📦 Bundle Deal — What's Included</p>
            <p className="text-purple-800 text-sm leading-relaxed whitespace-pre-line">{listing.bundleItems || "Multiple items included — ask seller for details."}</p>
          </div>
        )}

        {/* Description */}
        {listing.description && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Description</p>
            <p className="text-gray-700 text-sm leading-relaxed">{listing.description}</p>
          </div>
        )}

        {/* ── Rental Info Card ── */}
        {listing.isRental && listing.rental && (() => {
          const r = listing.rental;
          return (
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-lg">🏠</span>
                <p className="font-bold text-teal-800 text-sm">Rental Details</p>
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-3 gap-2">
                {r.pricePerDay && <div className="bg-white rounded-xl px-3 py-2 text-center border border-teal-100"><p className="text-base font-bold text-teal-700">₹{r.pricePerDay}</p><p className="text-xs text-gray-500">per day</p></div>}
                {r.pricePerWeek && <div className="bg-white rounded-xl px-3 py-2 text-center border border-teal-100"><p className="text-base font-bold text-teal-700">₹{r.pricePerWeek}</p><p className="text-xs text-gray-500">per week</p></div>}
                {r.pricePerMonth && <div className="bg-white rounded-xl px-3 py-2 text-center border border-teal-100"><p className="text-base font-bold text-teal-700">₹{r.pricePerMonth}</p><p className="text-xs text-gray-500">per month</p></div>}
              </div>

              {/* Info rows */}
              <div className="space-y-1.5 text-sm">
                {(r.minDuration || r.maxDuration) && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Duration</span>
                    <span className="font-medium text-gray-700">{r.minDuration || 1}–{r.maxDuration || "∞"} days</span>
                  </div>
                )}
                {r.deposit > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Security deposit</span>
                    <span className="font-medium text-teal-700">₹{r.deposit} <span className="text-gray-400 font-normal text-xs">(refundable)</span></span>
                  </div>
                )}
                {r.deliveryType && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Pickup / Delivery</span>
                    <span className="font-medium text-gray-700">
                      {{ pickup: "📍 Pickup only", delivery: "🚚 Delivery only", both: "🔄 Pickup or Delivery" }[r.deliveryType] || r.deliveryType}
                      {r.deliveryType !== "pickup" && r.deliveryCharge > 0 && ` (+₹${r.deliveryCharge})`}
                      {r.deliveryType !== "pickup" && r.deliveryCharge === 0 && " (free)"}
                    </span>
                  </div>
                )}
                {r.cancellationPolicy && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Cancellation</span>
                    <span className="font-medium text-gray-700">{{ free: "✅ Free cancellation", partial: "⚡ Partial refund", none: "❌ No refund" }[r.cancellationPolicy] || r.cancellationPolicy}</span>
                  </div>
                )}
                {r.lateFeePerDay > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Late return fee</span>
                    <span className="font-medium text-red-500">₹{r.lateFeePerDay}/day</span>
                  </div>
                )}
                {(r.availableFrom || r.availableTo) && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Availability</span>
                    <span className="font-medium text-gray-700">
                      {r.availableFrom ? new Date(r.availableFrom).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "Now"} – {r.availableTo ? new Date(r.availableTo).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "Open"}
                    </span>
                  </div>
                )}
              </div>

              {/* Booking done notice */}
              {bookingDone && myBooking && (
                <div className="bg-green-50 border border-green-200 rounded-xl px-3 py-2 text-sm text-green-700 font-medium">
                  ✅ Booking request sent! {bookStartDate} → {bookEndDate} · ₹{myBooking.totalPrice}
                  {myBooking.deposit > 0 && ` + ₹${myBooking.deposit} deposit`}
                  <p className="text-xs text-green-600 font-normal mt-0.5">Seller will confirm via notification. Status: Pending</p>
                </div>
              )}

              {/* Book button */}
              {currentUser && listing.sellerId !== currentUser.uid && listing.status === "active" && !bookingDone && (
                <button onClick={() => { setShowBookingModal(true); setBookingError(""); }}
                  className="w-full bg-teal-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-teal-700 transition">
                  📅 Book This Item
                </button>
              )}
            </div>
          );
        })()}

        {/* Price Drop Alert */}
        {currentUser && listing.sellerId !== currentUser.uid && listing.status === "active" && (
          <button
            onClick={togglePriceAlert}
            disabled={alertSaving}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border transition ${priceAlert ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-blue-50 hover:border-blue-300"}`}
          >
            {priceAlert ? <BellOff size={15} /> : <Bell size={15} />}
            {priceAlert ? "Remove Price Alert" : "Alert me if price drops"}
          </button>
        )}

        {/* Seller */}
        {!currentUser ? (
          <div className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-3 border border-dashed border-blue-200">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Lock size={20} className="text-blue-400" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-700 text-sm">Seller info is private</p>
              <p className="text-xs text-gray-400 mt-0.5">Login to see who's selling and their rating</p>
            </div>
            <a href="/login" className="bg-blue-600 text-white text-xs font-bold px-3 py-2 rounded-xl flex-shrink-0">Login</a>
          </div>
        ) : seller && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-blue-100">
              <p className="text-xs font-bold text-blue-600 uppercase tracking-wide">Sold by</p>
            </div>
            <div className="p-4">
              <div className="flex items-center gap-3">
                {/* Profile photo — tappable to open full profile */}
                <button
                  onClick={() => navigate(`/seller/${listing.sellerId}`)}
                  className="relative flex-shrink-0"
                >
                  <div className="w-16 h-16 rounded-full bg-blue-100 overflow-hidden flex items-center justify-center border-2 border-white shadow-md">
                    {seller.photoURL && canSeeSellerPhoto
                      ? <img src={seller.photoURL} alt={seller.name} className="w-full h-full object-cover" />
                      : <span className="text-blue-700 font-bold text-2xl">{seller.name?.[0]?.toUpperCase() || "?"}</span>}
                  </div>
                  {seller.verified && (
                    <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
                      <ShieldCheck size={10} className="text-white" />
                    </div>
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button onClick={() => navigate(`/seller/${listing.sellerId}`)} className="font-bold text-gray-800 hover:text-blue-600 transition text-base">{seller.name}</button>
                    {seller.verified && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold border border-green-200">✓ ID Verified</span>}
                    {!seller.verified && <span className="text-xs bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded-full font-semibold">Unverified</span>}
                    {seller.eduVerified && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-semibold">.edu ✓</span>}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{seller.college}</p>
                  {seller?.lastActive && (() => {
                    const mins = Math.floor((Date.now() - seller.lastActive) / 60000);
                    let text, color;
                    if (mins < 5) { text = "🟢 Active now"; color = "text-green-600"; }
                    else if (mins < 60) { text = `🟢 Active ${mins}m ago`; color = "text-green-600"; }
                    else if (mins < 1440) { text = `🔵 Active ${Math.floor(mins/60)}h ago`; color = "text-blue-500"; }
                    else { text = `⚫ Active ${Math.floor(mins/1440)}d ago`; color = "text-gray-400"; }
                    return <p className={`text-xs mt-0.5 font-medium ${color}`}>{text}</p>;
                  })()}
                  {seller.totalRatings > 0 && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <Star size={12} className="text-yellow-400 fill-yellow-400" />
                      <span className="text-xs text-gray-600 font-medium">{seller.rating?.toFixed(1)} <span className="text-gray-400 font-normal">({seller.totalRatings} reviews)</span></span>
                    </div>
                  )}
                </div>

                {currentUser && listing.sellerId !== currentUser.uid && (
                  <div className="flex flex-col items-end gap-1.5">
                    <button onClick={toggleBlockSeller} className={`p-1.5 rounded-lg ${sellerBlocked ? "text-red-500 bg-red-50" : "text-gray-300 hover:text-red-400"}`} title={sellerBlocked ? "Unblock seller" : "Block seller"}>
                      🚫
                    </button>
                    <button onClick={() => setShowReport(true)} className="flex items-center gap-1 text-xs text-red-500 border border-red-200 px-2 py-1 rounded-lg hover:bg-red-50 transition font-medium">
                      <Flag size={11} />
                      Report
                    </button>
                  </div>
                )}
              </div>

              {/* View full profile CTA */}
              {currentUser && listing.sellerId !== currentUser.uid && (
                <button
                  onClick={() => navigate(`/seller/${listing.sellerId}`)}
                  className="w-full mt-3 border border-blue-200 text-blue-600 text-sm font-semibold py-2 rounded-xl hover:bg-blue-50 transition flex items-center justify-center gap-2"
                >
                  <span>View Seller Profile</span>
                  <span className="text-gray-400">→</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Verification trust banner — shown to buyers only */}
        {currentUser && listing.sellerId !== currentUser?.uid && (
          listing.sellerVerified ? (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-3">
              <div className="w-9 h-9 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <ShieldCheck size={18} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-green-700">ID Verified Seller</p>
                <p className="text-xs text-green-600 mt-0.5">This seller's identity has been verified by CampusSwap admin. Safer to deal with.</p>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 flex items-start gap-3">
              <div className="w-9 h-9 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-lg">⚠️</span>
              </div>
              <div>
                <p className="text-sm font-bold text-yellow-800">Unverified Seller</p>
                <p className="text-xs text-yellow-700 mt-0.5">This seller hasn't submitted ID yet. Always meet in a safe public place and don't pay before seeing the item.</p>
              </div>
            </div>
          )
        )}

        {/* Verified seller perks — shown to the seller themselves */}
        {currentUser && listing.sellerId === currentUser?.uid && !listing.sellerVerified && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
            <p className="text-sm font-bold text-blue-800 mb-1">🔒 Get Verified — Sell More</p>
            <p className="text-xs text-gray-600 mb-2">Verified sellers get more trust, more buyers and better visibility.</p>
            <div className="grid grid-cols-2 gap-1.5 mb-3">
              {["✅ ID Verified badge on all listings","🔝 Higher in search results","💬 Buyers trust you more","⭐ Higher seller score"].map(perk => (
                <div key={perk} className="bg-white rounded-lg px-2 py-1.5 text-xs text-gray-700 font-medium">{perk}</div>
              ))}
            </div>
            <button onClick={() => navigate("/verify-id")} className="w-full bg-blue-600 text-white py-2 rounded-xl text-sm font-bold hover:bg-blue-700">
              Get Verified Free →
            </button>
          </div>
        )}

        {/* Seller Reviews — only for logged-in users */}
        {currentUser && sellerReviews.length > 0 && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Recent Reviews</p>
            <div className="space-y-3">
              {sellerReviews.map(r => (
                <div key={r.rid} className="border-b border-gray-50 pb-3 last:border-0 last:pb-0">
                  <div className="flex items-center gap-1 mb-1">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} size={11} className={s <= r.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"} />
                    ))}
                    <span className="text-xs text-gray-400 ml-1">{new Date(r.createdAt).toLocaleDateString("en-IN", { day:"numeric", month:"short" })}</span>
                  </div>
                  {r.comment && <p className="text-xs text-gray-600 italic">"{r.comment}"</p>}
                  <p className="text-xs text-gray-400 mt-0.5">for: {r.listingTitle}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cross-college request status (for buyer) */}
        {seller && currentUser && listing.sellerId !== currentUser.uid && (() => {
          const buyerCollege = userProfile?.college || userProfile?.childSchool;
          const sellerCollege = seller?.college || seller?.childSchool;
          // Only block as cross-college when BOTH colleges are known AND different
          const sameCollege = !buyerCollege || !sellerCollege || buyerCollege === sellerCollege;
          if (sameCollege) return null;
          if (myRequest?.status === "approved") {
            return (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-3">
                <span className="text-2xl">✅</span>
                <div>
                  <p className="text-sm font-semibold text-green-700">Request Approved!</p>
                  <p className="text-xs text-green-600 mt-0.5">The seller approved your request. You can now contact them below.</p>
                </div>
              </div>
            );
          }
          if (myRequest?.status === "pending") {
            return (
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center gap-3">
                <span className="text-2xl">⏳</span>
                <div>
                  <p className="text-sm font-semibold text-blue-700">Request Pending</p>
                  <p className="text-xs text-blue-600 mt-0.5">Waiting for the seller to approve your request to buy this item.</p>
                </div>
              </div>
            );
          }
          if (myRequest?.status === "rejected") {
            return (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3">
                <span className="text-2xl">❌</span>
                <div>
                  <p className="text-sm font-semibold text-red-700">Request Not Approved</p>
                  <p className="text-xs text-red-600 mt-0.5">The seller chose not to sell cross-college for this item.</p>
                </div>
              </div>
            );
          }
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <div className="flex items-center gap-3 mb-2">
                <Lock size={18} className="text-amber-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-700">Different College</p>
                  <p className="text-xs text-amber-600">Seller is from <strong>{sellerCollege}</strong>. Send a request to ask them to sell to you.</p>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Pending requests for seller */}
        {listing && currentUser && listing.sellerId === currentUser.uid && pendingRequests.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-blue-100 overflow-hidden">
            <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
              <p className="font-bold text-blue-800 text-sm">📬 Buy Requests from Other Colleges ({pendingRequests.length})</p>
            </div>
            <div className="divide-y divide-gray-50">
              {pendingRequests.map(req => (
                <div key={req.rid} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{req.buyerName}</p>
                      <p className="text-xs text-gray-500">{req.buyerCollege}</p>
                      {req.message && <p className="text-xs text-gray-600 mt-1 italic">"{req.message}"</p>}
                      <p className="text-xs text-gray-400 mt-0.5">{new Date(req.createdAt).toLocaleDateString("en-IN")}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleRequestAction(req, "approved")}
                      className="flex-1 bg-green-500 text-white py-2 rounded-xl text-xs font-bold hover:bg-green-600">
                      ✅ Approve
                    </button>
                    <button onClick={() => handleRequestAction(req, "rejected")}
                      className="flex-1 bg-red-100 text-red-600 py-2 rounded-xl text-xs font-bold hover:bg-red-200">
                      ❌ Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending offers for seller */}
        {listing && currentUser && listing.sellerId === currentUser.uid && pendingOffers.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-orange-100 overflow-hidden">
            <div className="px-4 py-3 bg-orange-50 border-b border-orange-100">
              <p className="font-bold text-orange-800 text-sm">💰 Price Offers ({pendingOffers.length})</p>
            </div>
            <div className="divide-y divide-gray-50">
              {pendingOffers.map(offer => (
                <div key={offer.oid} className="px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{offer.buyerName}</p>
                      <p className="text-base font-bold text-orange-600">₹{offer.price} <span className="text-xs text-gray-400 font-normal">(listed ₹{listing.price})</span></p>
                    </div>
                    <p className="text-xs text-gray-400">{new Date(offer.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                  </div>
                  {counterOfferMap[offer.oid] ? (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-600 flex-shrink-0">Counter ₹</span>
                      <input
                        type="number"
                        value={counterPriceMap[offer.oid] || ""}
                        onChange={e => setCounterPriceMap(prev => ({...prev, [offer.oid]: e.target.value}))}
                        placeholder="Your price"
                        className="flex-1 border border-blue-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 outline-none"
                        autoFocus
                      />
                      <button onClick={() => handleOfferAction(offer, "countered", counterPriceMap[offer.oid])}
                        className="bg-blue-600 text-white px-3 py-2 rounded-xl text-xs font-bold hover:bg-blue-700 flex-shrink-0">
                        Send
                      </button>
                      <button onClick={() => setCounterOfferMap(prev => { const n={...prev}; delete n[offer.oid]; return n; })}
                        className="text-gray-400 hover:text-gray-600 flex-shrink-0 px-1">✕</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={() => handleOfferAction(offer, "accepted")}
                        className="flex-1 bg-green-500 text-white py-2 rounded-xl text-xs font-bold hover:bg-green-600">
                        ✅ Accept
                      </button>
                      <button onClick={() => setCounterOfferMap(prev => ({...prev, [offer.oid]: true}))}
                        className="flex-1 bg-blue-50 text-blue-600 py-2 rounded-xl text-xs font-bold hover:bg-blue-100 border border-blue-200">
                        ↩️ Counter
                      </button>
                      <button onClick={() => handleOfferAction(offer, "declined")}
                        className="flex-1 bg-red-100 text-red-600 py-2 rounded-xl text-xs font-bold hover:bg-red-200">
                        ✗ Decline
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My offer status (for buyer) */}
        {myOffer && listing.sellerId !== currentUser?.uid && (
          myOffer.status === "countered" ? (
            <div className="rounded-xl px-4 py-3 bg-blue-50 border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">↩️</span>
                <p className="text-sm font-bold text-blue-800">Seller sent a counter-offer!</p>
              </div>
              <p className="text-xs text-blue-700 mb-3">Your offer ₹{myOffer.price} → Seller wants <span className="font-bold text-blue-900">₹{myOffer.counterPrice}</span></p>
              <div className="flex gap-2">
                <button onClick={() => handleCounterResponse("accepted")}
                  className="flex-1 bg-green-500 text-white py-2 rounded-xl text-xs font-bold hover:bg-green-600">
                  ✅ Accept ₹{myOffer.counterPrice}
                </button>
                <button onClick={() => handleCounterResponse("declined")}
                  className="flex-1 bg-red-100 text-red-600 py-2 rounded-xl text-xs font-bold hover:bg-red-200">
                  ✗ Decline
                </button>
              </div>
            </div>
          ) : (
            <div className={`rounded-xl px-4 py-3 flex items-center gap-3 ${
              myOffer.status === "accepted" ? "bg-green-50 border border-green-200" :
              myOffer.status === "declined" ? "bg-red-50 border border-red-200" :
              "bg-orange-50 border border-orange-200"
            }`}>
              <span className="text-2xl">
                {myOffer.status === "accepted" ? "✅" : myOffer.status === "declined" ? "❌" : "⏳"}
              </span>
              <div>
                <p className={`text-sm font-semibold ${myOffer.status === "accepted" ? "text-green-700" : myOffer.status === "declined" ? "text-red-700" : "text-orange-700"}`}>
                  {myOffer.status === "accepted" ? "Offer Accepted!" : myOffer.status === "declined" ? "Offer Declined" : "Offer Pending"}
                </p>
                <p className={`text-xs mt-0.5 ${myOffer.status === "accepted" ? "text-green-600" : myOffer.status === "declined" ? "text-red-600" : "text-orange-600"}`}>
                  Your offer of ₹{myOffer.price}
                  {myOffer.status === "accepted" ? " — Chat with the seller to arrange meet-up." :
                   myOffer.status === "declined" ? " — Try making a new offer or contact via WhatsApp." :
                   " — Waiting for seller to respond."}
                </p>
              </div>
            </div>
          )
        )}

        {/* WhatsApp Request Section — buyer view (only after cross-college approved or same college) */}
        {currentUser && listing.sellerId !== currentUser?.uid && listing.status === "active" && (() => {
          const bc = userProfile?.college || userProfile?.childSchool;
          const sc = seller?.college || seller?.childSchool;
          const sameCol = !bc || !sc || bc === sc;
          return sameCol || myRequest?.status === "approved";
        })() && (
          <div className="bg-green-50 border border-green-100 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageCircle size={16} className="text-green-600" />
              <p className="text-sm font-semibold text-gray-800">Chat on WhatsApp</p>
            </div>
            {myWARequest?.status === "approved" ? (
              <div className="space-y-2">
                <p className="text-xs text-green-700 font-medium">✅ Seller approved! You can now chat on WhatsApp.</p>
                <button onClick={openWhatsApp}
                  className="w-full bg-green-500 text-white py-2.5 rounded-xl text-sm font-bold hover:bg-green-600 flex items-center justify-center gap-2">
                  <MessageCircle size={16} /> Open WhatsApp
                </button>
              </div>
            ) : myWARequest?.status === "declined" ? (
              <p className="text-xs text-red-600">❌ Request declined by seller. Use in-app chat instead.</p>
            ) : myWARequest?.status === "pending" ? (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
                <p className="text-xs text-amber-700 font-medium">Request sent — waiting for seller to approve</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <p className="text-xs text-gray-500">The seller's number is private. Send a request — they'll approve if interested.</p>
                <button
                  onClick={requestWhatsApp}
                  disabled={waRequestLoading}
                  className="w-full bg-green-500 text-white py-2.5 rounded-xl text-sm font-bold hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2">
                  {waRequestLoading ? "Sending..." : "📱 Request WhatsApp"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Seller: WhatsApp requests to approve/decline */}
        {listing && currentUser && listing.sellerId === currentUser.uid && pendingWARequests.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-green-100 overflow-hidden">
            <div className="px-4 py-3 bg-green-50 border-b border-green-100">
              <p className="font-bold text-green-800 text-sm">📱 WhatsApp Requests ({pendingWARequests.length})</p>
              <p className="text-xs text-green-600 mt-0.5">Approve to share your number with the buyer</p>
            </div>
            <div className="divide-y divide-gray-50">
              {pendingWARequests.map(req => (
                <div key={req.wid} className="px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{req.buyerName}</p>
                      <p className="text-xs text-gray-500">{req.buyerCollege}</p>
                      <p className="text-xs text-gray-400">{new Date(req.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleWARequestAction(req, "approved")}
                      className="flex-1 bg-green-500 text-white py-2 rounded-xl text-xs font-bold hover:bg-green-600">
                      ✅ Approve
                    </button>
                    <button onClick={() => handleWARequestAction(req, "declined")}
                      className="flex-1 bg-red-100 text-red-600 py-2 rounded-xl text-xs font-bold hover:bg-red-200">
                      ✗ Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Safe Meet-up Zones */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <button onClick={() => setShowZones(!showZones)} className="w-full px-4 py-3 flex items-center justify-between text-left">
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-blue-500" />
              <span className="text-sm font-semibold text-gray-800">Safe Meet-up Zones</span>
            </div>
            <span className="text-xs text-gray-400">{showZones ? "▲" : "▼"}</span>
          </button>
          {showZones && (
            <div className="px-4 pb-4 grid grid-cols-2 gap-2">
              {SAFE_ZONES.map(zone => (
                <div key={zone} className="bg-blue-50 text-blue-700 text-xs px-3 py-2 rounded-xl font-medium">{zone}</div>
              ))}
              <p className="col-span-2 text-xs text-gray-400 mt-1">💡 Always exchange items in public places on campus</p>
            </div>
          )}
        </div>

        {/* Posted date */}
        {listing.createdAt && (
          <p className="text-xs text-gray-400">
            Posted {new Date(listing.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
          </p>
        )}

        {/* Buyer: Confirm Receipt */}
        {listing.status === "sold" &&
          listing.soldTo === currentUser?.uid &&
          !listing.buyerConfirmed &&
          listing.sellerId !== currentUser?.uid && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm font-bold text-gray-800 mb-1">Did you receive this item?</p>
            <p className="text-xs text-gray-500 mb-3">Please confirm receipt to complete the transaction and unlock the rating.</p>
            <button
              onClick={confirmReceipt}
              disabled={confirmingReceipt}
              className="w-full bg-blue-600 text-white py-2.5 rounded-xl font-bold text-sm hover:bg-blue-700 disabled:opacity-50">
              {confirmingReceipt ? "Confirming..." : "✅ Yes, I received it"}
            </button>
          </div>
        )}

        {/* Rating prompt for buyer */}
        {listing.status === "sold" &&
          (listing.reservedBy === currentUser?.uid || listing.soldTo === currentUser?.uid) &&
          !listing.buyerRated &&
          (listing.buyerConfirmed || listing.reservedBy === currentUser?.uid) &&
          listing.sellerId !== currentUser?.uid && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <p className="text-sm font-bold text-gray-800 mb-1">How was your experience?</p>
            <p className="text-xs text-gray-500 mb-3">Rate the seller to help other buyers</p>
            {ratingSubmitted ? (
              <p className="text-green-600 font-semibold text-sm text-center">⭐ Thanks for rating!</p>
            ) : (
              <>
                <div className="flex gap-2 justify-center mb-3">
                  {[1,2,3,4,5].map(star => (
                    <button key={star}
                      onMouseEnter={() => setRatingHover(star)}
                      onMouseLeave={() => setRatingHover(0)}
                      onClick={() => setRatingValue(star)}
                      className="text-3xl transition-transform hover:scale-110">
                      <span className={star <= (ratingHover || ratingValue) ? "text-yellow-400" : "text-gray-200"}>★</span>
                    </button>
                  ))}
                </div>
                {ratingValue > 0 && (
                  <>
                    <textarea
                      value={ratingComment}
                      onChange={e => setRatingComment(e.target.value)}
                      placeholder="Add a comment (optional)..."
                      rows={2}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mb-2 resize-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    />
                    <button onClick={submitRating}
                      className="w-full bg-yellow-400 text-yellow-900 font-bold py-2 rounded-xl text-sm hover:bg-yellow-500">
                      Submit Rating
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        )}
        {/* Similar Listings */}
        {similarListings.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide px-4 pt-4 pb-2">Similar Listings</p>
            <div className="divide-y divide-gray-50">
              {similarListings.map(s => (
                <button
                  key={s.id}
                  onClick={() => navigate(`/listing/${s.id}`)}
                  className="flex items-center gap-3 px-4 py-3 w-full text-left hover:bg-gray-50 transition"
                >
                  <div className="w-14 h-14 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0">
                    {s.imageUrl
                      ? <img src={s.imageUrl} alt={s.title} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-2xl">📦</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 line-clamp-1">{s.title}</p>
                    <p className="text-xs text-blue-600 font-bold mt-0.5">{s.price === 0 ? "Free" : `₹${s.price}`}</p>
                    <p className="text-xs text-gray-400">{s.condition}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Report Modal */}
      {showReport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
          <div className="bg-white w-full max-w-lg mx-auto rounded-t-2xl p-6" style={{ paddingBottom: "96px" }}>
            <h3 className="font-bold text-gray-800 mb-3">Report this listing</h3>
            <div className="space-y-2 mb-4">
              {["Fake / Scam listing", "Wrong category", "Inappropriate content", "Already sold elsewhere", "Other"].map(r => (
                <button key={r} onClick={() => setReportReason(r)}
                  className={`w-full text-left px-4 py-2.5 rounded-xl text-sm border ${reportReason === r ? "bg-red-50 border-red-300 text-red-700 font-semibold" : "border-gray-200 text-gray-700"}`}>
                  {r}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={submitReport} disabled={!reportReason}
                className="flex-1 bg-red-500 text-white py-3 rounded-xl font-semibold text-sm disabled:opacity-50">
                Submit Report
              </button>
              <button onClick={() => setShowReport(false)}
                className="flex-1 bg-gray-100 text-gray-600 py-3 rounded-xl font-semibold text-sm">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Buy Request Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4">
          <div className="bg-white w-full max-w-sm mx-auto rounded-2xl p-6 shadow-2xl">
            <h3 className="font-bold text-gray-800 mb-1">Send Buy Request</h3>
            <p className="text-xs text-gray-500 mb-1">You're from <strong>{userProfile?.college || userProfile?.childSchool}</strong>.</p>
            <p className="text-xs text-gray-500 mb-4">The seller will be notified and can approve your request to buy <strong>{listing?.title}</strong>.</p>
            <textarea
              value={requestMessage}
              onChange={e => setRequestMessage(e.target.value)}
              placeholder="Optional: Add a message to the seller (why you want it, your location, etc.)"
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm mb-4 resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="flex gap-2">
              <button onClick={sendBuyRequest} disabled={requestLoading}
                className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-bold text-sm disabled:opacity-50 hover:bg-blue-700">
                {requestLoading ? "Sending..." : "📩 Send Request"}
              </button>
              <button onClick={() => setShowRequestModal(false)}
                className="flex-1 bg-gray-100 text-gray-600 py-3 rounded-xl font-semibold text-sm">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Price Offer Modal */}
      {showOffer && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px", paddingBottom: "96px" }} onClick={() => setShowOffer(false)}>
          <div style={{ background: "#fff", width: "100%", maxWidth: "420px", borderRadius: "16px", padding: "24px" }} onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-gray-800 text-lg mb-1">Make an Offer</h3>
            <p className="text-xs text-gray-500 mb-4">Listed price: <strong>₹{listing.price}</strong> — Seller will be notified of your offer</p>
            {offerError && (
              <p className="text-red-500 text-xs bg-red-50 border border-red-100 rounded-xl px-3 py-2 mb-3">{offerError}</p>
            )}
            <div className="relative mb-5">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">₹</span>
              <input
                type="number"
                value={offerPrice}
                onChange={e => { const v = e.target.value; if (v === "" || Number(v) > 0) setOfferPrice(v); }}
                placeholder="Your offer price"
                min="1"
                className="w-full border border-gray-200 rounded-xl pl-8 pr-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={sendOffer} disabled={!offerPrice || Number(offerPrice) <= 0 || offerLoading}
                className="flex-1 bg-blue-600 text-white py-3.5 rounded-xl font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                {offerLoading
                  ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</>
                  : "Send Offer"}
              </button>
              <button onClick={() => { setShowOffer(false); setOfferError(""); }}
                className="flex-1 bg-gray-100 text-gray-600 py-3.5 rounded-xl font-semibold text-sm">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Rental Booking Modal ── */}
      {showBookingModal && listing?.rental && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={() => setShowBookingModal(false)}>
          <div style={{ background: "#fff", width: "100%", maxWidth: "512px", borderRadius: "20px 20px 0 0", padding: "24px", paddingBottom: "32px", maxHeight: "90vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4" />
            <h3 className="font-bold text-gray-800 text-lg mb-1">📅 Book Rental</h3>
            <p className="text-xs text-gray-500 mb-4">{listing.title}</p>

            {bookingError && <p className="text-red-500 text-xs bg-red-50 border border-red-100 rounded-xl px-3 py-2 mb-3">{bookingError}</p>}

            <div className="space-y-4">
              {/* Date Selection */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Start Date</label>
                  <input type="date" value={bookStartDate}
                    min={listing.rental.availableFrom || new Date().toISOString().split("T")[0]}
                    max={listing.rental.availableTo || undefined}
                    onChange={e => { setBookStartDate(e.target.value); setBookingError(""); }}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">End Date</label>
                  <input type="date" value={bookEndDate}
                    min={bookStartDate || listing.rental.availableFrom || new Date().toISOString().split("T")[0]}
                    max={listing.rental.availableTo || undefined}
                    onChange={e => { setBookEndDate(e.target.value); setBookingError(""); }}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent" />
                </div>
              </div>

              {/* Availability hint */}
              {(listing.rental.availableFrom || listing.rental.availableTo) && (
                <p className="text-xs text-teal-600 bg-teal-50 rounded-xl px-3 py-2">
                  📅 Available: {listing.rental.availableFrom ? new Date(listing.rental.availableFrom).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "Now"} – {listing.rental.availableTo ? new Date(listing.rental.availableTo).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "Open-ended"}
                </p>
              )}

              {/* Duration hint */}
              {(listing.rental.minDuration > 1 || listing.rental.maxDuration) && (
                <p className="text-xs text-gray-500">Minimum {listing.rental.minDuration || 1} day{listing.rental.minDuration > 1 ? "s" : ""}{listing.rental.maxDuration ? `, maximum ${listing.rental.maxDuration} days` : ""}</p>
              )}

              {/* Price preview */}
              {bookStartDate && bookEndDate && (() => {
                const pr = calcRentalPrice(listing.rental, bookStartDate, bookEndDate);
                if (!pr) return null;
                return (
                  <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 space-y-1.5">
                    <p className="text-xs font-semibold text-teal-800 uppercase tracking-wide">Price Breakdown</p>
                    <p className="text-xs text-teal-700">{pr.desc}</p>
                    {listing.rental.deposit > 0 && (
                      <p className="text-xs text-gray-500">+ ₹{listing.rental.deposit} refundable deposit</p>
                    )}
                    {listing.rental.deliveryType !== "pickup" && listing.rental.deliveryCharge > 0 && (
                      <p className="text-xs text-gray-500">+ ₹{listing.rental.deliveryCharge} delivery</p>
                    )}
                    <div className="border-t border-teal-200 pt-1.5 mt-1">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-bold text-teal-800">Rental Total</span>
                        <span className="text-lg font-bold text-teal-700">₹{pr.total + (listing.rental.deposit || 0) + (listing.rental.deliveryType !== "pickup" ? (listing.rental.deliveryCharge || 0) : 0)}</span>
                      </div>
                      <p className="text-xs text-gray-400">(Rental ₹{pr.total}{listing.rental.deposit > 0 ? ` + ₹${listing.rental.deposit} deposit` : ""})</p>
                    </div>
                  </div>
                );
              })()}

              {/* Cancellation policy */}
              {listing.rental.cancellationPolicy && (
                <p className="text-xs text-gray-500">Cancellation: {{ free: "✅ Free cancellation", partial: "⚡ Partial refund", none: "❌ No refund" }[listing.rental.cancellationPolicy]}</p>
              )}

              {/* Delivery type */}
              {listing.rental.deliveryType && (
                <p className="text-xs text-gray-500">
                  {{ pickup: "📍 Pickup only", delivery: "🚚 Delivery available", both: "🔄 Pickup or delivery" }[listing.rental.deliveryType]}
                </p>
              )}

              <div className="flex gap-3 pt-1">
                <button onClick={submitRentalBooking} disabled={!bookStartDate || !bookEndDate || bookingLoading}
                  className="flex-1 bg-teal-600 text-white py-3.5 rounded-xl font-semibold text-sm hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {bookingLoading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Booking...</> : "Request Booking"}
                </button>
                <button onClick={() => { setShowBookingModal(false); setBookingError(""); }}
                  className="flex-1 bg-gray-100 text-gray-600 py-3.5 rounded-xl font-semibold text-sm">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sticky CTA */}
      <div className={`fixed left-0 right-0 bg-white border-t border-gray-100 p-3 max-w-lg mx-auto z-40 ${currentUser ? "bottom-16" : "bottom-0"}`}>
        {!currentUser ? (
          <div className="flex gap-2">
            <a href="/login" className="flex-1 bg-blue-600 text-white py-3 rounded-2xl font-bold text-sm flex items-center justify-center hover:bg-blue-700">
              Login to Buy / Chat
            </a>
            <a href="/signup" className="flex-1 bg-yellow-400 text-blue-900 py-3 rounded-2xl font-bold text-sm flex items-center justify-center hover:bg-yellow-500">
              Sign Up Free
            </a>
          </div>
        ) : listing.status === "sold" ? (
          <div className="space-y-2">
            <div className="w-full bg-gray-100 text-gray-500 py-3 rounded-2xl font-bold text-base flex items-center justify-center gap-2">
              <X size={20} /> This item has been SOLD
            </div>
            {listing.sellerId === currentUser?.uid && (
              <button onClick={async () => {
                await update(ref(db, `listings/${id}`), { status: "active" });
                setListing(prev => ({ ...prev, status: "active" }));
              }} className="w-full bg-blue-600 text-white py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-blue-700">
                Relist — Mark as Available Again
              </button>
            )}
          </div>

        ) : listing.status === "pending_purchase" && listing.buyingBy === currentUser?.uid ? (
          // Buyer: 5-min purchase window
          <div className="space-y-2">
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-center">
              <p className="text-amber-700 text-sm font-bold">
                ⏳ {countdown !== null ? `${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, "0")} remaining` : "..."}
              </p>
              <p className="text-amber-600 text-xs mt-0.5">Seller must confirm within 5 min — chat to arrange meetup & payment</p>
            </div>
            <button onClick={startChat} disabled={chatLoading}
              className="w-full bg-blue-600 text-white py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-blue-700 disabled:opacity-70">
              {chatLoading ? "⏳ Starting chat..." : "💬 Chat with Seller — Arrange Payment & Meetup"}
            </button>
            <div className="flex gap-2">
              {myWARequest?.status === "approved"
                ? <button onClick={openWhatsApp} className="flex-1 bg-green-500 text-white py-2.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-1.5 hover:bg-green-600"><MessageCircle size={16} /> WhatsApp</button>
                : <button onClick={requestWhatsApp} disabled={!!myWARequest || waRequestLoading} className="flex-1 bg-green-50 text-green-600 py-2.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-1.5 border border-green-200 disabled:opacity-60 text-xs">{myWARequest ? "WA Pending…" : "📱 Request WA"}</button>
              }
              <button onClick={cancelBuyNow}
                className="px-4 bg-red-100 text-red-600 py-2.5 rounded-2xl font-bold text-sm flex items-center justify-center hover:bg-red-200">
                <X size={18} />
              </button>
            </div>
          </div>

        ) : listing.status === "pending_purchase" && listing.sellerId === currentUser?.uid ? (
          // Seller: confirm or cancel the incoming purchase
          <div className="space-y-2">
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-center">
              <p className="text-amber-700 text-sm font-bold">
                🛍️ A buyer wants this!{countdown !== null ? ` ${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, "0")} left` : ""}
              </p>
              <p className="text-amber-600 text-xs mt-0.5">Chat to arrange cash/UPI payment & meetup, then confirm sale</p>
            </div>
            <button onClick={startChat} disabled={chatLoading}
              className="w-full bg-blue-600 text-white py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-blue-700 disabled:opacity-70">
              {chatLoading ? "⏳ Starting chat..." : "💬 Chat with Buyer — Arrange Payment & Meetup"}
            </button>
            <div className="flex gap-2">
              <button onClick={confirmSale}
                className="flex-1 bg-green-500 text-white py-2.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-green-600">
                ✅ Confirm Sale
              </button>
              <button onClick={cancelBuyNow}
                className="px-4 bg-red-100 text-red-600 py-2.5 rounded-2xl font-bold text-sm flex items-center justify-center hover:bg-red-200">
                <X size={18} />
              </button>
            </div>
          </div>

        ) : listing.status === "pending_purchase" ? (
          // Someone else is buying
          <div className="w-full bg-amber-50 border border-amber-200 text-amber-700 py-4 rounded-2xl font-bold text-sm flex items-center justify-center gap-2">
            🛍️ Being purchased — {countdown !== null ? `${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, "0")} remaining` : "check back soon"}
          </div>

        ) : listing.status === "reserved" && listing.reservedBy === currentUser?.uid ? (
          <div className="space-y-2">
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-3 py-2 text-center">
              <p className="text-yellow-700 text-xs font-semibold">Reserved for 12 hrs — auto-releases if no deal</p>
            </div>
            <div className="flex gap-2">
              {myWARequest?.status === "approved"
                ? <button onClick={openWhatsApp} className="flex-1 bg-green-500 text-white py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-1.5 hover:bg-green-600"><MessageCircle size={18} /> WhatsApp</button>
                : <button onClick={requestWhatsApp} disabled={!!myWARequest || waRequestLoading} className="flex-1 bg-green-50 text-green-600 py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-1.5 border border-green-200 disabled:opacity-60">{myWARequest ? "WA Pending…" : "📱 Request WA"}</button>
              }
              <button onClick={startChat} disabled={chatLoading}
                className="flex-1 bg-blue-500 text-white py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-1.5 hover:bg-blue-600 disabled:opacity-60">
                {chatLoading ? "⏳" : "💬 Chat"}
              </button>
              <button onClick={cancelReservation}
                className="px-4 bg-red-100 text-red-600 py-3 rounded-2xl font-bold text-sm flex items-center justify-center hover:bg-red-200">
                <X size={18} />
              </button>
            </div>
          </div>

        ) : listing.status === "paused" && listing.sellerId !== currentUser?.uid ? (
          <div className="w-full bg-gray-100 text-gray-500 py-4 rounded-2xl font-bold text-sm flex items-center justify-center gap-2">
            ⏸️ This listing is temporarily paused
          </div>

        ) : listing.status === "paused" && listing.sellerId === currentUser?.uid ? (
          <div className="space-y-2">
            <div className="w-full bg-gray-100 text-gray-500 py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2">
              ⏸️ Listing is Paused — hidden from other students
            </div>
            <button onClick={async () => {
              await update(ref(db, `listings/${id}`), { status: "active" });
              setListing(prev => ({ ...prev, status: "active" }));
            }} className="w-full bg-blue-600 text-white py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-blue-700">
              ▶️ Resume Listing
            </button>
          </div>

        ) : listing.status === "reserved" ? (
          <div className="w-full bg-yellow-50 border border-yellow-200 text-yellow-700 py-4 rounded-2xl font-bold text-sm flex items-center justify-center gap-2">
            <Clock size={18} /> Someone is negotiating — check back soon
          </div>

        ) : listing.sellerId === currentUser?.uid ? (
          <div className="space-y-2">
            <button onClick={async () => {
              await update(ref(db, `listings/${id}`), { status: "sold" });
              setListing(prev => ({ ...prev, status: "sold" }));
            }} className="w-full bg-green-600 text-white py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-green-700">
              ✅ Mark as Sold
            </button>
            {listing.isRental && (
              <button onClick={() => navigate("/rental-bookings")}
                className="w-full flex items-center justify-center gap-2 text-sm text-teal-600 font-semibold bg-teal-50 py-2.5 rounded-xl border border-teal-200 hover:bg-teal-100">
                📅 View Rental Booking Requests
              </button>
            )}
            <div className="flex gap-2">
              <button onClick={() => navigate(`/sell?edit=${id}`)}
                className="flex-1 flex items-center justify-center gap-1 text-sm text-blue-600 font-semibold bg-blue-50 py-2.5 rounded-xl border border-blue-200 hover:bg-blue-100">
                ✏️ Edit Listing
              </button>
              <button onClick={async () => {
                if (!window.confirm("Delete this listing permanently?")) return;
                await remove(ref(db, `listings/${id}`));
                navigate("/my-listings");
              }} className="flex-1 flex items-center justify-center gap-1 text-sm text-red-500 font-semibold bg-red-50 py-2.5 rounded-xl border border-red-200 hover:bg-red-100">
                🗑️ Delete
              </button>
            </div>
          </div>

        ) : (() => {
          const buyerCollege = userProfile?.college || userProfile?.childSchool;
          const sellerCollege = seller?.college || seller?.childSchool;
          const sameCollege = !buyerCollege || !sellerCollege || buyerCollege === sellerCollege;
          const canContact = sameCollege || myRequest?.status === "approved";
          if (!canContact) {
            if (myRequest?.status === "pending") {
              return (
                <div className="w-full bg-blue-50 border border-blue-200 text-blue-700 py-3.5 px-4 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2">
                  ⏳ Request sent — waiting for seller approval
                </div>
              );
            }
            if (myRequest?.status === "rejected") {
              return (
                <div className="w-full bg-red-50 border border-red-200 text-red-600 py-3.5 px-4 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2">
                  ❌ Request was declined by seller
                </div>
              );
            }
            return (
              <button onClick={() => setShowRequestModal(true)}
                className="w-full bg-blue-600 text-white py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-blue-700 active:scale-95 transition">
                📩 Send Buy Request to Seller
              </button>
            );
          }
          return (
            <div className="space-y-2">
              <button onClick={handleBuyNow}
                className="w-full bg-blue-600 text-white py-3.5 rounded-2xl font-bold text-base flex items-center justify-center gap-2 hover:bg-blue-700 active:scale-95 transition">
                🛒 Buy Now
              </button>
              <div className="flex gap-2">
                {myWARequest?.status === "approved"
                  ? <button onClick={openWhatsApp} className="flex-1 bg-green-500 text-white py-2.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-1 hover:bg-green-600"><MessageCircle size={15} /> WhatsApp</button>
                  : <button onClick={requestWhatsApp} disabled={!!myWARequest || waRequestLoading} className="flex-1 bg-green-50 text-green-600 py-2.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-1 hover:bg-green-100 border border-green-200 disabled:opacity-60">{myWARequest?.status === "pending" ? "WA Pending…" : myWARequest?.status === "declined" ? "WA Declined" : "📱 Request WA"}</button>
                }
                <button onClick={startChat} disabled={chatLoading}
                  className="flex-1 bg-blue-50 text-blue-600 py-2.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-1 hover:bg-blue-100 border border-blue-200 disabled:opacity-60">
                  {chatLoading ? "⏳" : "💬 Chat"}
                </button>
                <button onClick={() => setShowOffer(true)}
                  className="flex-1 bg-orange-50 text-orange-600 py-2.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-1 hover:bg-orange-100 border border-orange-200">
                  <Tag size={14} /> Offer
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
